import axiosInstance from '../api';

export type EscalationStatus = 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'DISMISSED';

export type EscalationUrgency = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

export type EscalationReason =
  | 'USER_REQUESTED'
  | 'HIGH_STAKES_ACTION'
  | 'NEGATIVE_SENTIMENT'
  | 'LEGAL_QUESTION'
  | 'COMPLEX_NEGOTIATION'
  | 'DEADLINE_RISK'
  | 'NO_RESPONSE'
  | 'ERROR_RECOVERY'
  | 'POLICY_VIOLATION'
  | 'LOW_CONFIDENCE_EXTRACTION'
  | 'NEW_INBOUND_LEAD'
  | 'LEAD_INTAKE_FAILED'
  | 'ESTIMATE_LINK_READY'
  | 'DEAL_UPDATE_UNCLEAR'
  | 'INSPECTION_REPORT_UPLOADED';

/** Where the escalation came from, written onto `context` by `recordEscalation`. */
export type EscalationSource =
  | 'REVA_AGENT'
  | 'AGENT_TASK'
  | 'CUSTOMER_REQUEST'
  | 'SYSTEM_FAILURE'
  | 'LEAD_INTAKE'
  | 'CUSTOMER_UPLOAD';

interface EscalationPerson {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface Escalation {
  id: string;
  workflowId?: string | null;
  taskId?: string | null;
  reason: EscalationReason;
  urgency: EscalationUrgency;
  status: EscalationStatus;
  context: Record<string, any>;
  resolution?: Record<string, any> | null;
  resolutionNote?: string | null;
  assigneeId?: string | null;
  resolvedById?: string | null;
  createdAt: string;
  resolvedAt?: string | null;

  assignee?: EscalationPerson | null;
  resolvedBy?: EscalationPerson | null;
  workflow?: {
    id: string;
    type: string;
    currentState: string;
    status: string;
    transactionId?: string | null;
    user?: EscalationPerson | null;
    transaction?: {
      id: string;
      listings?: {
        id: string;
        address: string;
        city: string;
        state: string;
      } | null;
    } | null;
  } | null;
}

export interface EscalationQuery {
  status?: EscalationStatus | EscalationStatus[];
  urgency?: EscalationUrgency | EscalationUrgency[];
  reason?: EscalationReason | EscalationReason[];
  $limit?: number;
  $skip?: number;
}

export interface PaginatedEscalations {
  total: number;
  limit: number;
  skip: number;
  data: Escalation[];
}

const escalationsService = {
  find: async (query?: EscalationQuery): Promise<PaginatedEscalations> => {
    const response = await axiosInstance.get<PaginatedEscalations>('/escalations', {
      params: query,
    });
    return response.data;
  },

  get: async (id: string): Promise<Escalation> => {
    const response = await axiosInstance.get<Escalation>(`/escalations/${id}`);
    return response.data;
  },

  /** Claim the escalation — "I have this." */
  acknowledge: async (id: string): Promise<Escalation> => {
    const response = await axiosInstance.patch<Escalation>(`/escalations/${id}`, {
      status: 'IN_PROGRESS',
    });
    return response.data;
  },

  /** Close it out. The note is required by the API — it is the record of what was done. */
  resolve: async (id: string, resolutionNote: string): Promise<Escalation> => {
    const response = await axiosInstance.patch<Escalation>(`/escalations/${id}`, {
      status: 'RESOLVED',
      resolutionNote,
    });
    return response.data;
  },

  /** Close it without action — nothing here needed a human after all. */
  dismiss: async (id: string, resolutionNote?: string): Promise<Escalation> => {
    const response = await axiosInstance.patch<Escalation>(`/escalations/${id}`, {
      status: 'DISMISSED',
      ...(resolutionNote ? { resolutionNote } : {}),
    });
    return response.data;
  },
};

export default escalationsService;
